import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Button,
  Chip,
  Fade,
  useTheme,
  alpha
} from '@mui/material';
import {
  LocationCity as SiteIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Assignment as ActivityIcon,
  Schedule as TimeIcon,
  Badge as AraIcon
} from '@mui/icons-material';
import styled from 'styled-components';
import { apiCall } from "../../../services/ApiCall";
import config from "../../../config/config.json";
import { useNavigate } from "react-router-dom";

// Styled Components with #0d6efd theme and white text/icons
const StyledContainer = styled(Box)`
  background: linear-gradient(135deg, #f8faff 0%, #e3f2fd 100%);
  min-height: 100vh;
  padding: 24px;
`;

const HeaderCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border-radius: 20px !important;
  box-shadow: 0 8px 32px rgba(13, 110, 253, 0.12) !important;
  border: 1px solid rgba(13, 110, 253, 0.1);
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
  }
`;

const TableCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border-radius: 20px !important;
  box-shadow: 0 8px 32px rgba(13, 110, 253, 0.1) !important;
  border: 1px solid rgba(13, 110, 253, 0.08);
  overflow: hidden;
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 12px !important;
    background: rgba(255, 255, 255, 0.9);
    transition: all 0.3s ease !important;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 4px 12px rgba(13, 110, 253, 0.1);
    }
    
    &.Mui-focused {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.2);
    }
  }
  
  .MuiInputLabel-root {
    font-weight: 500;
    
    &.Mui-focused {
      color: #0d6efd;
    }
  }
`;

const StyledTableContainer = styled(TableContainer)`
  border-radius: 16px !important;
  max-height: 500px;
  
  .MuiTable-root {
    background: transparent;
  }
`;

const StyledTableHead = styled(TableHead)`
  .MuiTableCell-head {
    background: #0a58ca !important;
    backdrop-filter: blur(20px);
    color: white !important;
    font-weight: 700 !important;
    font-size: 13px !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 16px !important;
    border-bottom: 2px solid rgba(13, 110, 253, 0.1) !important;
    border-top: none !important;
    position: sticky;
    top: 0;
    z-index: 10;
    cursor: pointer;
    transition: all 0.3s ease !important;
    
    &:hover {
      background: rgba(248, 250, 255, 0.9) !important;
      color: #0d6efd !important;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(13, 110, 253, 0.08);
      
      .header-icon {
        transform: scale(1.1);
        color: #0d6efd !important;
      }
      
      .header-text {
        color: #0d6efd !important;
      }
    }
    
    &:first-of-type {
      border-top-left-radius: 16px;
    }
    
    &:last-of-type {
      border-top-right-radius: 16px;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 0;
      height: 2px;
      background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
      transition: all 0.3s ease;
      transform: translateX(-50%);
    }
    
    &:hover::after {
      width: 80%;
    }
  }
`;

const HeaderContent = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  
  .header-icon {
    transition: all 0.3s ease;
    color: white;
    font-size: 18px;
  }
  
  .header-text {
    transition: all 0.3s ease;
    color: white;
    font-weight: 700;
    letter-spacing: 0.3px;
  }
`;

const StyledTableRow = styled(TableRow)`
  transition: all 0.2s ease !important;
  
  &:hover {
    background: rgba(13, 110, 253, 0.04) !important;
    transform: scale(1.001);
  }
  
  &:nth-of-type(even) {
    background: rgba(248, 250, 255, 0.5);
  }
`;

const StyledTableCell = styled(TableCell)`
  padding: 16px !important;
  border-bottom: 1px solid rgba(13, 110, 253, 0.1) !important;
  color: #1a202c !important;
  font-weight: 500 !important;
  font-size: 14px !important;
`;

const ActivityChip = styled(Chip)`
  background: ${props => getActivityColor(props.activity).background} !important;
  color: ${props => getActivityColor(props.activity).color} !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
`;

// Helper function for activity colors
const getActivityColor = (activity) => {
  const activityLower = activity?.toLowerCase() || '';
  
  if (activityLower.includes('create') || activityLower.includes('add')) {
    return { background: alpha('#4caf50', 0.1), color: '#4caf50' };
  } else if (activityLower.includes('update') || activityLower.includes('edit')) {
    return { background: alpha('#ff9800', 0.1), color: '#ff9800' };
  } else if (activityLower.includes('delete') || activityLower.includes('remove')) {
    return { background: alpha('#f44336', 0.1), color: '#f44336' };
  } else {
    return { background: alpha('#0d6efd', 0.1), color: '#0d6efd' };
  }
};

const SiteHistory = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Original state variables - keeping exactly the same
  const [data, setData] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [editData, setEditData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  // Original functions - keeping exactly the same logic
  const handleChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const getSitesHistoryList = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getSitesHistoryList`,
      {},
      {},
      "GET"
    );
    if (isSuccess) {
      setData(data?.data?.reverse());
    }
  };

  // Original useEffect - keeping exactly the same
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    }
    getSitesHistoryList();
  }, []);

  // Original date formatting - keeping exactly the same
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
  
    hours = hours % 12 || 12;
    hours = String(hours).padStart(2, '0');
  
    return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
  };

  // Original search handler - keeping exactly the same
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Original filtering logic - keeping exactly the same
  const filteredData = data.filter((item) => {
    const companyName = item.companyName || "";
    const araId = item.araId || "";
    const activityBy = item.name || "";
    const address = item.address || "";
    const activity = item.activity || "";
    const createdAt = item.createdAt || "";
    const fullAddress = `${item?.address} ${item?.city} ${item?.state}`.toLowerCase();

    return (
      companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      araId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activityBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
      address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.toLowerCase().includes(searchTerm.toLowerCase()) ||
      createdAt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fullAddress.includes(searchTerm.toLowerCase()) 
    );
  });

  return (
    <StyledContainer>
      {/* Header Section */}
      <HeaderCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SiteIcon sx={{ color: '#0d6efd', mr: 2, fontSize: 32 }} />
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.5
                  }}
                >
                  Site History
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>
                  Track all site-related activities and changes
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                label={`${filteredData.length} Records`}
                sx={{ 
                  background: alpha('#0d6efd', 0.1),
                  color: '#0d6efd',
                  fontWeight: 600
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      {/* Search Section */}
      <Fade in timeout={500}>
        <Card sx={{ 
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          border: '1px solid rgba(13, 110, 253, 0.08)',
          mb: 3
        }}>
          <CardContent sx={{ p: 3 }}>
            <StyledTextField
              fullWidth
              placeholder="Search by Company, ARA ID, Activity, Address, or User..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
            />
          </CardContent>
        </Card>
      </Fade>

      {/* Table Section */}
      <Fade in timeout={700}>
        <TableCard>
          <StyledTableContainer>
            <Table stickyHeader>
              <StyledTableHead>
                <TableRow>
                  <TableCell>
                    <HeaderContent>
                      <BusinessIcon className="header-icon" />
                      <span className="header-text">Company</span>
                    </HeaderContent>
                  </TableCell>
                  <TableCell>
                    <HeaderContent>
                      <AraIcon className="header-icon" />
                      <span className="header-text">ARA ID</span>
                    </HeaderContent>
                  </TableCell>
                  <TableCell>
                    <HeaderContent>
                      <PersonIcon className="header-icon" />
                      <span className="header-text">User</span>
                    </HeaderContent>
                  </TableCell>
                  <TableCell>
                    <HeaderContent>
                      <LocationIcon className="header-icon" />
                      <span className="header-text">Address</span>
                    </HeaderContent>
                  </TableCell>
                  <TableCell>
                    <HeaderContent>
                      <ActivityIcon className="header-icon" />
                      <span className="header-text">Activity</span>
                    </HeaderContent>
                  </TableCell>
                  <TableCell>
                    <HeaderContent>
                      <TimeIcon className="header-icon" />
                      <span className="header-text">When</span>
                    </HeaderContent>
                  </TableCell>
                </TableRow>
              </StyledTableHead>
              
              <TableBody>
                {filteredData.map((item, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a202c' }}>
                        {item?.companyName}
                      </Typography>
                    </StyledTableCell>
                    
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AraIcon sx={{ color: '#0d6efd', fontSize: 16 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item?.araId}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: '#0d6efd', fontSize: 16 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item?.name}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {`${item?.address} ${item?.city} ${item?.state}`}
                      </Typography>
                    </StyledTableCell>
                    
                    <StyledTableCell>
                      <ActivityChip
                        label={item?.activity}
                        size="small"
                        activity={item?.activity}
                      />
                    </StyledTableCell>
                    
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a202c' }}>
                        {formatDateTime(item?.createdAt)}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
                
                {filteredData.length === 0 && (
                  <StyledTableRow>
                    <StyledTableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" sx={{ color: '#64748b' }}>
                        No records found matching your search criteria
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </TableCard>
      </Fade>
    </StyledContainer>
  );
};

export default SiteHistory;